import { Component, EventEmitter, Input, Output, TemplateRef, OnChanges, SimpleChanges, ViewChild, OnDestroy } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { IonItemSliding, ItemReorderEventDetail } from '@ionic/angular';

import { ScrollService } from '../scroll-service/scroll-service';
import { getUniqueId, snapshot } from '../utils';
import { getValueFromTranslationSync } from '../language';

@Component({
    standalone: false,
    selector: 'abs-list',
    host: { class: 'list-flush' },
    template: `
        @if (displayRows$ | async; as displayRows) {
            <ng-template #rowInner let-item="item" let-itemSliding="itemSliding" let-i="i">
                <ion-item
                    *ngIf="getRouterLink"
                    [routerLink]="getRouterLink(item)"
                    [queryParams]="getQueryParams ? getQueryParams(item) : null"
                    lines="none"
                    [class.active]="(isRowActive && isRowActive(item, i)) || (!!activeStyleField && !!item[activeStyleField])"
                    [class.alt]="item.alt || (item.alt === undefined && i % 2 === 0)"
                    [class.clickable]="itemHover"
                >
                    <ion-reorder slot="start" *ngIf="reorderable">
                        <ion-icon name="reorder-three-outline"></ion-icon>
                    </ion-reorder>
                    <ng-template
                        [ngTemplateOutlet]="template ?? defaultTemplate"
                        [ngTemplateOutletContext]="{ item: item, index: i }"
                    ></ng-template>

                    <div class="itemOptions" *ngIf="customRowButtonsTemplate || showDelete || menuTemplate">
                        @if (itemOptionsMode === 'menu') {
                            <ion-button
                                fill="clear"
                                size="small"
                                color="medium"
                                (click)="rowMenuBtn.present($event); $event.stopImmediatePropagation(); $event.preventDefault()"
                            >
                                <ion-icon slot="icon-only" name="ellipsis-vertical"></ion-icon>
                            </ion-button>
                            <ion-popover #rowMenuBtn dismissOnSelect="true" side="bottom" alignment="end">
                                <ng-template>
                                    <ng-template
                                        *ngIf="menuTemplate"
                                        [ngTemplateOutlet]="menuTemplate"
                                        [ngTemplateOutletContext]="{ item: item, index: i }"
                                    ></ng-template>
                                </ng-template>
                            </ion-popover>
                        } @else {
                            <ng-template
                                *ngIf="customRowButtonsTemplate"
                                [ngTemplateOutlet]="customRowButtonsTemplate"
                                [ngTemplateOutletContext]="{ item: item, index: i }"
                            ></ng-template>

                            <ion-button
                                *ngIf="item.__showDelete"
                                fill="clear"
                                color="danger"
                                size="small"
                                (click)="deleteInline($event, item)"
                            >
                                <ion-icon slot="icon-only" class="deleteIcon" name="trash"></ion-icon>
                            </ion-button>
                        }
                    </div>
                </ion-item>
                <ion-item
                    *ngIf="!getRouterLink"
                    (click)="itemClicked($event, item, i)"
                    (press)="itemLongPressed(item, i)"
                    lines="none"
                    [class.alt]="item.alt || (item.alt === undefined && i % 2 === 0)"
                    [class.active]="(isRowActive && isRowActive(item, i)) || (!!activeStyleField && !!item[activeStyleField])"
                    [class.clickable]="itemHover"
                >
                    <ion-reorder slot="start" *ngIf="reorderable">
                        <ion-icon name="reorder-three-outline"></ion-icon>
                    </ion-reorder>
                    <ng-template
                        [ngTemplateOutlet]="template ?? defaultTemplate"
                        [ngTemplateOutletContext]="{ item: item, index: i }"
                    ></ng-template>

                    <div class="itemOptions" *ngIf="customRowButtonsTemplate || showDelete || menuTemplate">
                        @if (itemOptionsMode === 'menu') {
                            <ion-button
                                fill="clear"
                                size="small"
                                color="medium"
                                (click)="rowMenuBtn2.present($event); $event.stopImmediatePropagation(); $event.preventDefault()"
                            >
                                <ion-icon slot="icon-only" name="ellipsis-vertical"></ion-icon>
                            </ion-button>
                            <ion-popover #rowMenuBtn2 dismissOnSelect="true" side="bottom" alignment="end">
                                <ng-template>
                                    <ng-template
                                        *ngIf="menuTemplate"
                                        [ngTemplateOutlet]="menuTemplate"
                                        [ngTemplateOutletContext]="{ item: item, index: i }"
                                    ></ng-template>
                                </ng-template>
                            </ion-popover>
                        } @else {
                            <ng-template
                                *ngIf="customRowButtonsTemplate"
                                [ngTemplateOutlet]="customRowButtonsTemplate"
                                [ngTemplateOutletContext]="{ item: item, index: i }"
                            ></ng-template>
                            <ion-button
                                *ngIf="item.__showDelete"
                                fill="clear"
                                color="danger"
                                size="small"
                                (click)="deleteInline($event, item)"
                            >
                                <ion-icon slot="icon-only" class="deleteIcon" name="trash"></ion-icon>
                            </ion-button>
                        }
                    </div>
                </ion-item>
                <ion-item-options side="end" *ngIf="customRowButtonsTemplate || showDelete">
                    <ng-template
                        *ngIf="customRowButtonsTemplate"
                        [ngTemplateOutlet]="customRowButtonsTemplate"
                        [ngTemplateOutletContext]="{ item: item, index: i }"
                    ></ng-template>
                    <ion-item-option
                        class="delete deleteIcon"
                        *ngIf="item.__showDelete"
                        (click)="delete($event, item, itemSliding)"
                        expandable
                        ><ion-icon name="trash"></ion-icon
                    ></ion-item-option>
                </ion-item-options>
            </ng-template>

            <ng-template #defaultTemplate let-item="item">
                <div class="itemName">
                    <span>{{ item.name | language | async }}</span>
                </div>
            </ng-template>

            <ng-template #defaultHeaderTemplate let-item="item">
                <ion-label>{{ item.name }}</ion-label>
                <ion-icon
                    *ngIf="collapsibleGroups"
                    [name]="visibleGroups[item.id] ? 'caret-down-outline' : 'caret-up-outline'"
                    (click)="toggleGroup(item.id)"
                ></ion-icon>
            </ng-template>

            @if (showSearch) {
                <ion-searchbar (ionInput)="updateSearch($event)" [value]="searchVal"></ion-searchbar>
            }
            <ion-spinner *ngIf="showLoadingSpinner && !this.items"></ion-spinner>
            <ion-list>
                <ion-reorder-group [disabled]="!reorderable || !!searchVal" (ionItemReorder)="onReorder($event)">
                    @for (item of displayRows; track trackByFn($index, item); let i = $index) {
                        @if (!item.isHeader && item.isPassthrough) {
                            <ng-template
                                [ngTemplateOutlet]="template ?? defaultTemplate"
                                [ngTemplateOutletContext]="{ item: item, index: i }"
                            ></ng-template>
                        } @else {
                            <div
                                class="row"
                                [attr.data-row-key]="item.__rowKey"
                                [class.deleted]="item.deleted"
                                [class.showItemOptions]="
                                    (customRowButtonsTemplate || showDelete || menuTemplate) && itemOptionsVisibility !== 'always'
                                "
                                [class.alwaysShowItemOptions]="
                                    (customRowButtonsTemplate || showDelete || menuTemplate) && itemOptionsVisibility === 'always'
                                "
                            >
                                <ion-item-divider *ngIf="item.isHeader && item.name" [class]="dividerCssClass || ''">
                                    <ng-template
                                        [ngTemplateOutlet]="headerTemplate ?? defaultHeaderTemplate"
                                        [ngTemplateOutletContext]="{ item: item, index: i }"
                                    ></ng-template>
                                </ion-item-divider>
                                <ion-item-sliding
                                    *ngIf="!item.isHeader && item.__swipeable"
                                    #itemSliding
                                    (ionSwipe)="onIonSwipe($event, item, itemSliding)"
                                >
                                    <ng-template
                                        [ngTemplateOutlet]="rowInner"
                                        [ngTemplateOutletContext]="{ item: item, itemSliding: itemSliding, i: i, index: i }"
                                    ></ng-template>
                                </ion-item-sliding>

                                <ng-template
                                    *ngIf="!item.isHeader && !item.__swipeable"
                                    [ngTemplateOutlet]="rowInner"
                                    [ngTemplateOutletContext]="{ item: item, itemSliding: null, i: i, index: i }"
                                ></ng-template>
                            </div>
                        }
                    }
                </ion-reorder-group>
            </ion-list>

            <div *ngIf="noItemsTextKey && items?.length === 0" class="page-placeholder content">
                {{ noItemsTextKey | translate }}
            </div>
            <div *ngIf="noItemsTemplate && items?.length === 0" class="page-placeholder content">
                <ng-template [ngTemplateOutlet]="noItemsTemplate" [ngOutletContext]="context"></ng-template>
            </div>

            <div *ngIf="showSearch && noItemsFromSearchTextKey && displayRows?.length === 0" class="page-placeholder content">
                {{ noItemsFromSearchTextKey | translate }}
            </div>
            <div *ngIf="showSearch && noItemsFromSearchTemplate && displayRows?.length === 0" class="page-placeholder content">
                <ng-template [ngTemplateOutlet]="noItemsFromSearchTemplate" [ngOutletContext]="context"></ng-template>
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                position: relative;
                margin-bottom: 20px;
                margin-top: 10px;
            }

            ion-list,
            ion-item {
                flex: 1 1 auto;
                display: flex;
                flex-direction: column;
                --background: none;
                --ion-item-background: none;
                padding: 0;

                /* These were changed to support positioning the period component in the top left on the Bolt Action add unit page.  
                If they need to be removed then be sure to set them in the Bolt Action stylesheet instead so that page doesn't break. */
                contain: unset;
                overflow: visible;
            }

            ion-item-divider {
                padding: 0;
                padding-left: var(--gutter);
            }

            .clickable {
                cursor: pointer;
            }

            .clickable:hover:before {
                content: '';
                background: rgba(0, 0, 0, 0.1);
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                pointer-events: none;
                z-index: 2;
            }

            abs-icon {
                margin-right: 10px;
            }

            .row {
                display: flex;
                justify-items: end;
                align-items: center;
                width: 100%;
            }

            .deleted {
                opacity: 0;
                margin-left: -100%;
                transition: all 0.35s;
            }

            ion-spinner {
                margin: 10px auto;
            }

            .itemOptions {
                display: none;
            }

            @media (min-width: 601px) {
                .showItemOptions ion-item {
                    --inner-padding-end: 0 !important;
                }
                .showItemOptions ion-item-sliding {
                    flex: 1;
                }
                .showItemOptions .itemOptions {
                    display: flex;
                    align-items: center;
                    padding: 0 8px;
                }
                .showItemOptions .itemOptions ion-icon {
                    margin: 0 8px;
                }
            }

            .alwaysShowItemOptions ion-item {
                --inner-padding-end: 0 !important;
            }
            .alwaysShowItemOptions ion-item-sliding {
                flex: 1;
            }
            .alwaysShowItemOptions .itemOptions {
                display: flex;
                align-items: center;
                padding: 0 8px;
            }
            .alwaysShowItemOptions .itemOptions ion-icon {
                margin: 0 8px;
            }
        `
    ]
})
export class ListComponent implements OnChanges, OnDestroy {
    @Input() reorderable = false;
    @Input() itemOptionsMode: 'inline' | 'menu' = 'inline';
    @Input() itemOptionsVisibility: 'always' | 'responsive' = 'responsive';
    @Input() menuTemplate: TemplateRef<any> = null;
    @Input() getQueryParams: (item: any) => any = null;
    @Input() isSwipeable: (item: any) => boolean = null;
    @Output() itemReorder = new EventEmitter<{ from: number; to: number }>();

    @Input() debugId: string;
    @Input() showLoadingSpinner = true;
    @Input() template: TemplateRef<any> = null;
    @Input() customRowButtonsTemplate: TemplateRef<any> = null;
    @Input() headerTemplate: TemplateRef<any> = null;
    @Input() dividerCssClass: string = null;
    @Input() items: any[] = [];
    @Input() noItemsTextKey: string = null;
    @Input() noItemsTemplate: TemplateRef<any> = null;
    @Input() noItemsFromSearchTextKey: string = null;
    @Input() noItemsFromSearchTemplate: TemplateRef<any> = null;
    @Input() activeStyleField: string = null;
    @Input() showSearch: boolean = false;
    @Input() showDelete: boolean | ((item: any) => boolean) = false;
    @Input() includeInSearchOverrideField: string = 'selected';
    @Input() searchVal = '';
    @Output()
    searchValueUpdated = new EventEmitter<string>();

    @Input() itemHover = true;
    @Input() isRowActive: (item: any, index: number) => boolean;
    @Input() getRouterLink: (item: any) => string[];
    @Output() itemSelect = new EventEmitter<{ e: any; item: any; i: number }>();
    @Output() itemLongPress = new EventEmitter<{ item: any; i: number }>();
    @Output() deleteItem = new EventEmitter<{
        item: any;
        i: number;
        slidingItem: IonItemSliding;
        callback: (deleted: boolean) => void;
    }>();

    firstRender = true;

    @ViewChild('defaultTemplate') defaultTemplate;
    @ViewChild('defaultHeaderTemplate') defaultHeaderTemplate;

    @Input() groupItems = true;
    @Input() groupBy: string = null;
    @Input() groupNames: string[];
    @Input() groupNameField: string = null;
    @Input() showEmptyGroups: boolean = false;
    @Input() collapsibleGroups: boolean = false;
    currentHeader: string;
    visibleGroups = {};

    @Input() trackBy: (i: number, item: any) => string;

    displayRows$ = new ReplaySubject<any[]>();
    private currentRows: any[] = [];
    itemListIds: number[]; // Used to find the index of an item being deleted
    offsetIndex: number[];

    destroy$ = new ReplaySubject();

    constructor(
        private router: Router,
        private scrollService: ScrollService,
        private route: ActivatedRoute
    ) {}

    trackByFn(i, item) {
        if (this.trackBy) {
            return this.trackBy(i, item);
        }
        let trackingKey = '';

        if (item.id) {
            trackingKey += item.id + ':';
        } else if (item._id) {
            trackingKey += item._id + ':';
        }

        if (item.key) {
            trackingKey += item.key;
        } else if (item.name) {
            let name = item.name;
            if (item.name.en) {
                name = getValueFromTranslationSync(item.name);
            }
            trackingKey += name;
        }

        if (item.count !== undefined && item.count !== null) {
            trackingKey += ':' + item.count;
        }

        if (!trackingKey) {
            trackingKey = i;
        }
        return trackingKey;
    }

    ngOnChanges(changes: SimpleChanges): void {
        for (const propName in changes) {
            if (changes.hasOwnProperty(propName)) {
                switch (propName) {
                    case 'items': {
                        if (changes.items.currentValue && changes.items.currentValue !== changes.items.previousValue) {
                            this.updateItems();
                        }
                        break;
                    }
                    // The per-row flags are stamped once in updateItems() rather than read through a
                    // method in the template, so they go stale if what they derive from changes
                    // without `items` changing with it. That is the normal case for a force locked by
                    // an event: the submission is fetched async, so showDelete flips to false well
                    // after these rows were built, and without this the row would keep the trash icon
                    // and the swipe it was rendered with.
                    case 'showDelete':
                    case 'isSwipeable':
                    case 'itemOptionsMode': {
                        this.refreshRowFlags();
                        break;
                    }
                }
            }
        }
    }

    filterBySearchValue = (item: any, searchVal: string) => {
        const translatedName = item.name?.en ? getValueFromTranslationSync(item.name) : item.name;
        return !searchVal || item[this.includeInSearchOverrideField] || translatedName.toUpperCase().includes(searchVal.toUpperCase());
    };

    updateSearch(e: any) {
        const value = e.detail.value;
        this.searchVal = value;
        this.updateItems();
        this.searchValueUpdated.emit(this.searchVal);
    }

    async updateItems() {
        if (!this.items) {
            return;
        }
        this.currentHeader = '';
        const allItemsAndHeaders = [
            ...this.items
                .filter((x) => !!x && !x.hide)
                .filter((x) => this.filterBySearchValue(x, this.searchVal))
                .sort((a, b) => {
                    if (!this.groupItems || !this.groupBy) {
                        return 0;
                    }
                    if (this.groupNames?.length > 0) {
                        return this.groupNames.indexOf(a[this.groupBy]) - this.groupNames.indexOf(b[this.groupBy]);
                    }
                    return a[this.groupBy]?.toString().localeCompare(b[this.groupBy]?.toString());
                })
                .map((e, i) => ({
                    ...e,
                    alt: i % 2 === 0,
                    uniqueListId: getUniqueId()
                }))
        ];

        this.itemListIds = allItemsAndHeaders.map((x) => x.uniqueListId);

        if (this.groupItems && this.groupBy) {
            let groups = this.groupNames?.map((x) => ({ id: x, name: x }));

            const groupNameField = this.groupNameField || this.groupBy;
            const itemGroups = allItemsAndHeaders.map((x) => ({ id: x[this.groupBy], name: x[groupNameField] }));
            if (!groups || groups.length === 0) {
                groups = itemGroups.filter((g, i) => itemGroups.findIndex((x) => x.id === g.id) === i);
            }

            // Insert groupname rows
            let currentItemIndex = 0;
            for (let i = 0; i < groups.length; i++) {
                const group = groups[i];
                let headersAdded = 0;
                if (group && group.name) {
                    allItemsAndHeaders.splice(currentItemIndex, 0, { isHeader: true, ...group } as any);
                    headersAdded = 1;
                }

                if (this.visibleGroups[group.id] === undefined) {
                    this.visibleGroups[group.id] = true;
                }

                const itemCount = allItemsAndHeaders.filter((x) => x[this.groupBy] === group.id).length;
                currentItemIndex += itemCount + headersAdded;
            }
        }

        const finalRows = allItemsAndHeaders.filter(
            (x) => !this.groupItems || !x[this.groupBy || 'groupKey'] || this.visibleGroups[x[this.groupBy || 'groupKey']]
        );

        // A stable handle on each rendered row, so a consumer can find one in the DOM -- to scroll
        // it into view, or highlight it after the list has changed underneath the user. Assigned
        // here rather than bound through a template method, per the no-methods-in-templates rule.
        //
        // NON-ENUMERABLE deliberately. These are the caller's own objects, and some consumers pass
        // items that get persisted or synced (forces, collections). An enumerable property would
        // ride along through JSON.stringify and object spreads into saved data.
        finalRows.forEach((row: any) => {
            Object.defineProperty(row, '__rowKey', {
                value: row.isHeader ? `header:${row.id}` : `row:${row.id ?? row._id ?? ''}`,
                enumerable: false,
                configurable: true,
                writable: true
            });
            this.stampRowFlags(row);
        });

        this.currentRows = finalRows;
        this.displayRows$.next(finalRows);
    }

    // Non-enumerable for the same reason as __rowKey: these rows are the caller's own objects, and
    // some of them get persisted or synced.
    private stampRowFlags(row: any) {
        Object.defineProperty(row, '__showDelete', {
            value: this.showDeleteFor(row),
            enumerable: false,
            configurable: true,
            writable: true
        });
        Object.defineProperty(row, '__swipeable', {
            value: this.swipeableFor(row),
            enumerable: false,
            configurable: true,
            writable: true
        });
    }

    // Re-stamps in place rather than rebuilding and re-emitting the rows: change detection picks the
    // new values up on the next pass, and the list is spared a sort/group/emit cycle on every change
    // to a lock flag.
    private refreshRowFlags() {
        this.currentRows.forEach((row: any) => this.stampRowFlags(row));
    }

    onReorder(ev: CustomEvent<ItemReorderEventDetail>): void {
        this.itemReorder.emit({ from: ev.detail.from, to: ev.detail.to });
        ev.detail.complete(false); // Angular owns the DOM; displayRows$ drives re-render
    }

    // showDelete can be a plain boolean (applies to every row) or a per-item predicate --
    // added so a consumer can hide the delete affordance for individual rows (e.g. a locked
    // item) without disabling delete for the whole list. Always read through this rather than
    // `this.showDelete` directly wherever a specific row's own visibility is being decided.
    showDeleteFor(item: any): boolean {
        return typeof this.showDelete === 'function' ? this.showDelete(item) : this.showDelete;
    }

    // Whether this row gets a sliding container at all. A row that cannot be deleted is not made
    // swipeable, so a locked force's units and platoons simply do not move under the finger --
    // rather than sliding open on an empty options panel and then deleting on the full swipe.
    //
    // customRowButtonsTemplate deliberately does NOT appear here: those options are non-destructive
    // (duplicate, move) and every list that has them also withdraws them when the row is locked, so
    // treating a truthy template as "this row is swipeable" only ever resurrected the gesture on
    // rows that had just had it taken away. Menu-mode lists are the exception -- they supply their
    // own delete inside the swipe options instead of through showDelete -- so they stay swipeable
    // and gate individual rows with isSwipeable.
    swipeableFor(item: any): boolean {
        if (this.isSwipeable && !this.isSwipeable(item)) {
            return false;
        }
        return this.showDeleteFor(item) || this.itemOptionsMode === 'menu';
    }

    onIonSwipe(event: any, item: any, slidingItem: IonItemSliding): void {
        if (this.swipeableFor(item)) {
            this.delete(event, item, slidingItem);
        } else {
            slidingItem.close();
        }
    }

    itemClicked(e: any, item: any, i: number) {
        this.itemSelect.emit({ e, item: item, i });
    }

    itemLongPressed(item: any, i: number) {
        this.itemLongPress.emit({ item: item, i });
    }

    isNaN(value: any) {
        return isNaN(value);
    }

    async delete(event: any, item: any, slidingItem: IonItemSliding) {
        event.stopPropagation();
        event.preventDefault();
        await slidingItem.close();

        // The row animates out before the consumer has even been asked, on the assumption that the
        // delete will go through (the consumer offers an Undo toast instead of a confirmation). That
        // assumption only holds while the row is genuinely deletable, so re-check here: a row whose
        // delete has been withdrawn must not be animated away on the strength of a delete that the
        // consumer is about to refuse.
        if (!this.swipeableFor(item)) {
            return;
        }

        const itemOnlyIndex = this.itemListIds.indexOf(item.uniqueListId);

        const setItemDeleted = (deleted: boolean) => {
            item.deleted = deleted;
            snapshot(this.displayRows$, (displayRows) => {
                const allRowsIndex = displayRows.indexOf(item);
                if (displayRows[allRowsIndex]) {
                    displayRows[allRowsIndex].deleted = deleted;
                }
            });
        };
        setItemDeleted(true);
        setTimeout(() => {
            this.deleteItem.emit({ item: item, i: itemOnlyIndex, slidingItem, callback: setItemDeleted });
        }, 350);
    }

    deleteInline(event: any, item: any) {
        event.stopPropagation();
        event.preventDefault();
        // Same optimistic animation as delete(), so the same re-check -- see the note there.
        if (!this.showDeleteFor(item)) {
            return;
        }
        const itemOnlyIndex = this.itemListIds.indexOf(item.uniqueListId);
        const setItemDeleted = (deleted: boolean) => {
            item.deleted = deleted;
            snapshot(this.displayRows$, (displayRows) => {
                const allRowsIndex = displayRows.indexOf(item);
                if (displayRows[allRowsIndex]) {
                    displayRows[allRowsIndex].deleted = deleted;
                }
            });
        };
        setItemDeleted(true);
        setTimeout(() => {
            this.deleteItem.emit({ item: item, i: itemOnlyIndex, slidingItem: null, callback: setItemDeleted });
        }, 350);
    }

    ngOnDestroy() {
        console.log(this.debugId, 'ngOnDestroy');
        this.destroy$.next(true);
    }

    toggleGroup(groupKey) {
        this.visibleGroups[groupKey] = !this.visibleGroups[groupKey];
        this.updateItems();
    }
}

// export interface NamedItem {
//     name: Translation;
//     isHeader?: number;
//     yPos: number;
//     height: number;
//     alt?: boolean;
//     deleted?: boolean; // used to animate a deleted item disappearing
//     hide?: boolean;
// }
